import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

// The socket is now a NUDGE transport only (design §1). It no longer stores notifications —
// the durable inbox (GET /api/v1/notifications) is the source of truth, rendered by
// NotificationBell. The one notification event that matters is `inbox:new`; on it we dispatch
// a window event so the bell re-fetches. `connected` is still exposed as a secondary qualifier
// for the bell's status line.
const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user) return;

    // Production: same-origin via the nginx /socket.io proxy. Dev: API server directly.
    const s = io(process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3000', {
      transports: ['websocket', 'polling']
    });

    s.on('connect', () => {
      setConnected(true);
      s.emit('join', { role: user.role, name: user.name, userId: user.id });
    });
    s.on('disconnect', () => setConnected(false));

    // ── The durable-inbox nudge — the only notification event now (design §1). ──
    // Carries a count/timestamp, not content; the bell re-fetches its inbox on receipt.
    const nudge = () => window.dispatchEvent(new Event('inbox:new'));
    s.on('inbox:new', nudge);

    // Cross-component live refreshes (NOT bell notifications) — kept as-is so screens that
    // listen for these window events (My HR, Risk Sign-offs, dashboards) keep updating live.
    s.on('dashboard:refresh', () => window.dispatchEvent(new Event('dashboard:refresh')));
    s.on('hr:reliever_request',  (d) => window.dispatchEvent(new CustomEvent('hr:reliever_request', { detail: d })));
    s.on('hr:reliever_response', (d) => window.dispatchEvent(new CustomEvent('hr:reliever_response', { detail: d })));
    s.on('risk:signoff_request', (d) => window.dispatchEvent(new CustomEvent('risk:signoff_request', { detail: d })));

    // ── Six orphaned bell events — KEPT, awaiting business-event emitters (ruling 3). ──
    // production:log, po:overdue, qc:alert, order:confirmed, ta:late, machine:breakdown are
    // listened for but emitted by nothing yet; those modules are not functional. Do NOT delete —
    // the listeners stay ready. When each module is wired, the correct emitter is a backend
    // notify() call (durable inbox row + inbox:new nudge + email), not a bespoke socket event; a
    // fired legacy event here still nudges the durable bell to re-fetch so nothing is lost.
    ['production:log', 'po:overdue', 'qc:alert', 'order:confirmed', 'ta:late', 'machine:breakdown']
      .forEach(evt => s.on(evt, nudge));

    setSocket(s);
    return () => { s.disconnect(); };
  }, [user]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
